/**
 * Просмотрщик медиа (лайтбокс) — поверх интерфейса, в том же окне.
 *
 * ПОЧЕМУ НЕ НОВАЯ ВКЛАДКА (как было с <a target="_blank">): Антон, патч
 * 2026-09-10 10:15 — «должно открываться в том же фрейме». К тому же новая
 * вкладка с blob:-URL умирала вместе с размонтированием превью в ленте:
 * URL.revokeObjectURL из useAuthedMedia убивал картинку в соседней вкладке.
 *
 * УСТРОЙСТВО:
 *  - список медиа комнаты приходит снаружи (Timeline знает про события,
 *    лайтбокс — нет); открыт один слайд за раз;
 *  - загрузка ЛЕНИВАЯ: качается ТОЛЬКО текущий слайд, один раз на слайд —
 *    ViewerBody (key=eventId) зовёт useAuthedMediaBlob и раздаёт blob и
 *    шапке (кнопки), и сцене (картинка/видео). Соседние слайды НЕ
 *    предзагружаем сознательно: каждый файл = авторизованный fetch в память
 *    (см. шапку media.ts), и комната с сотней фото при предзагрузке
 *    положила бы вкладку. Листание на большом файле покажет «загружаю…» —
 *    это честнее, чем убитый браузер;
 *  - blob:-URL освобождается автоматически: ViewerBody размонтируется при
 *    перелистывании/закрытии, и cleanup хука зовёт URL.revokeObjectURL;
 *  - закрытие тремя способами: клик по затемнённому фону (только по фону,
 *    не по картинке), крестик, Esc. Стрелки ← → листают с клавиатуры.
 *
 * КНОПКИ:
 *  - «Скачать» — в обычном браузере Blob сохраняется прямо из страницы
 *    временной <a download> на blob: без target=_blank (Opera GX не
 *    проходила путь через service worker, Антон 04.11 20:14). В оболочках
 *    (Mac WKWebView, Android) Blob, как и раньше, передаётся service worker
 *    и отдаётся как same-origin HTTPS attachment /__alg-download/<id>;
 *    Electron получает байты через ALGDesktop.saveAttachment;
 *  - «Поделиться» — Web Share API с files. Кнопку показываем, только если
 *    navigator.canShare({files}) подтверждает поддержку: desktop Firefox
 *    файлы шарить не умеет, и серая кнопка там была бы обманом.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { tf, useT, useTF } from "../i18n";
import type { MatrixClient } from "matrix-js-sdk";

import { useAuthedMediaBlob } from "../matrix/media";
import type { TimelineItem } from "../matrix/timeline";
import { canShareFiles, shareFile } from "./share";
import { Zoomable } from "./Zoomable";
import {
  IconChevronLeft,
  IconChevronRight,
  IconClose,
  IconDownload,
  IconShare,
} from "./icons";
import { color, font, radius, space } from "./theme";
import { downloadReceivedAttachment } from "../downloads/receivedAttachmentDownload";

/** Одна позиция в просмотрщике — то же вложение, что рисует лента. */
export type ViewerMedia = NonNullable<TimelineItem["attachment"]> & {
  /** eventId сообщения — ключ слайда и маркер для тестов. */
  eventId: string;
  /** Время события — показываем в шапке рядом с размером. */
  ts: number;
};

/**
 * Иконка типа файла для карточки без превью.
 *
 * По расширению имени, а не только по MIME: отправители регулярно шлют
 * «application/octet-stream» на всё подряд, и тогда единственная правда —
 * хвост имени файла. Совпадение грубое (по окончанию), для значка достаточно.
 */
export function fileIconOf(name: string, mimetype: string): string {
  const n = name.toLowerCase();
  const mime = mimetype.toLowerCase();
  if (mime === "application/pdf" || n.endsWith(".pdf")) return "📄";
  if (
    n.endsWith(".doc") || n.endsWith(".docx") || n.endsWith(".odt") ||
    n.endsWith(".rtf") || mime.includes("word") || mime.includes("opendocument.text")
  ) return "📝";
  if (
    n.endsWith(".xls") || n.endsWith(".xlsx") || n.endsWith(".ods") || n.endsWith(".csv") ||
    mime.includes("excel") || mime.includes("spreadsheet") || mime === "text/csv"
  ) return "📊";
  if (
    n.endsWith(".ppt") || n.endsWith(".pptx") || n.endsWith(".odp") ||
    mime.includes("presentation")
  ) return "📽️";
  if (
    n.endsWith(".zip") || n.endsWith(".rar") || n.endsWith(".7z") ||
    n.endsWith(".tar") || n.endsWith(".gz") || n.endsWith(".bz2") ||
    mime.includes("zip") || mime.includes("compressed") || mime.includes("tar")
  ) return "🗜️";
  if (
    n.endsWith(".txt") || n.endsWith(".md") || n.endsWith(".log") ||
    n.endsWith(".json") || n.endsWith(".xml") || n.endsWith(".yaml") || n.endsWith(".yml") ||
    mime.startsWith("text/")
  ) return "📃";
  if (
    n.endsWith(".apk") || n.endsWith(".exe") || n.endsWith(".dmg") ||
    n.endsWith(".deb") || n.endsWith(".msi")
  ) return "📦";
  return "📎";
}

/** Красивый размер: «12 КБ», «3,4 МБ». Пустая строка, если размера нет. */
export function humanSize(bytes: number): string {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return tf("common.sizeBytes", { size: bytes });
  const kb = bytes / 1024;
  if (kb < 1024) return tf("common.sizeKb", { size: Math.max(1, Math.round(kb)) });
  const mb = kb / 1024;
  // Один знак после запятой до 10 МБ, дальше целые: «9,8 МБ», но «24 МБ».
  const text = mb < 10 ? mb.toFixed(1).replace(".", ",") : String(Math.round(mb));
  return tf("common.sizeMb", { size: text });
}

export function MediaViewer({
  client,
  media,
  index,
  onClose,
  onNavigate,
}: {
  client: MatrixClient;
  /** Все медиа комнаты в порядке ленты (старые → новые). */
  media: ViewerMedia[];
  /** Индекс текущего слайда в media. */
  index: number;
  onClose: () => void;
  /** Перейти к слайду по индексу (границы проверяет родитель). */
  onNavigate: (index: number) => void;
}) {
  const hasPrev = index > 0;
  const hasNext = index < media.length - 1;

  // Клавиатура: Esc — закрыть, ← → — листать. Вешаем на window: фокус в
  // лайтбоксе может оказаться где угодно (кнопка, видео), а клавиши обязаны
  // работать всегда, пока просмотрщик открыт.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // stopPropagation: Esc не должен заодно закрыть меню/диалог ПОД
        // нами — просмотрщик поверх всего, клавиши его.
        e.stopPropagation();
        onClose();
      } else if (e.key === "ArrowLeft" && index > 0) {
        onNavigate(index - 1);
      } else if (e.key === "ArrowRight" && index < media.length - 1) {
        onNavigate(index + 1);
      }
    };
    // capture=true — по той же причине: первыми клавишу видим мы.
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose, onNavigate, index, media.length]);

  // Скролл body под лайтбоксом блокируем: колесо мыши над картинкой не
  // должно крутить ленту под затемнением.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const current = media[index];
  if (!current) return null;

  return (
    <div
      style={{
        // absolute, а не fixed: просмотрщик ограничен ОКНОМ ЧАТА (s.main
        // задаёт position:relative), список комнат остаётся видимым и
        // кликабельным. Требование Антона от 2026-09-10 10:43.
        position: "absolute",
        inset: 0,
        background: "rgba(0, 0, 0, 0.82)",
        zIndex: 70,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
      data-testid="media-viewer"
      onClick={(e) => {
        // Закрытие ТОЛЬКО по фону: клик по картинке/кнопкам не закрывает,
        // иначе промах мимо стрелки гасил бы просмотр.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* key=eventId обязателен: смена слайда = размонтирование старого
          ViewerBody и монтирование нового, иначе blob:-URL и состояние
          загрузки переехали бы на ЧУЖОЙ файл, а revoke старого не случился
          бы никогда. */}
      <ViewerBody
        key={current.eventId}
        client={client}
        media={current}
        index={index}
        total={media.length}
        hasPrev={hasPrev}
        hasNext={hasNext}
        onClose={onClose}
        onNavigate={onNavigate}
      />
    </div>
  );
}

/**
 * Тело одного слайда: ОДНА загрузка (useAuthedMediaBlob), раздача результата
 * и верхней панели (кнопки), и сцене (картинка/видео/карточка файла).
 * Вынесено ради этого единственного fetch на слайд: качать файл дважды
 * (отдельно для шапки и для сцены) — двойной трафик на каждый просмотр.
 */
function ViewerBody({
  client,
  media,
  index,
  total,
  hasPrev,
  hasNext,
  onClose,
  onNavigate,
}: {
  client: MatrixClient;
  media: ViewerMedia;
  index: number;
  total: number;
  hasPrev: boolean;
  hasNext: boolean;
  onClose: () => void;
  onNavigate: (index: number) => void;
}) {
  const t = useT();
  const { url, blob, error } = useAuthedMediaBlob(client, media.mxc);
  // Увеличен ли текущий слайд (Zoomable сообщает). В увеличенном виде
  // стрелки листания прячем: они лежат ПОВЕРХ увеличенной картинки, клик
  // по ним сменил бы слайд «из-под» зума, а палец, тянущий картинку к краю,
  // попадал бы по стрелке. Клавиши ← → продолжают работать (MediaViewer),
  // но и там: зум сбрасывается при смене слайда, key=eventId.
  const [isZoomed, setIsZoomed] = useState(false);

  return (
    <>
      {/* Верхняя панель: имя файла + действия + крестик. */}
      <div
        style={{
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          gap: space.lg,
          padding: `${space.lg}px ${space.xl}px`,
          color: "#fff",
        }}
        // Панель — не фон: клики по ней (кроме кнопок) не закрывают.
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
          <div
            style={{
              fontSize: font.body,
              fontWeight: font.weightBold,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            data-testid="media-viewer-name"
          >
            {media.name}
          </div>
          <div style={{ fontSize: font.tiny, opacity: 0.75 }} data-testid="media-viewer-meta">
            {humanSize(media.size)}
            {` · ${new Date(media.ts).toLocaleString()}`}
            {total > 1 && ` · ${index + 1} / ${total}`}
          </div>
        </div>
        <ViewerActions blob={blob} media={media} />
        <button
          type="button"
          onClick={onClose}
          title={t("ui.zakryt_esc")}
          data-testid="media-viewer-close"
          style={chromeButtonStyle}
        >
          <IconClose size={18} />
        </button>
      </div>

      {/* Центр: стрелка | слайд | стрелка. Фон кликабелен для закрытия. */}
      <div
        style={{
          flex: "1 1 auto",
          display: "flex",
          alignItems: "center",
          minHeight: 0,
          position: "relative",
        }}
      >
        {hasPrev && !isZoomed && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(index - 1);
            }}
            title={t("ui.staree")}
            data-testid="media-viewer-prev"
            style={{ ...chromeButtonStyle, ...navButtonStyle, left: space.xl }}
          >
            <IconChevronLeft size={22} />
          </button>
        )}
        <div
          style={{
            flex: "1 1 auto",
            height: "100%",
            display: "grid",
            placeItems: "center",
            minWidth: 0,
            minHeight: 0,
            // ОБЯЗАТЕЛЬНО: глобального box-sizing в проекте нет, и без этого
            // padding прибавлялся к height:100% — картинка вылезала за низ.
            boxSizing: "border-box",
            overflow: "hidden",
            padding: `0 72px ${space.xl}px`,
          }}
          // Клик мимо содержимого (по этой подложке) тоже закрывает.
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <Slide media={media} url={url} error={error} onIsZoomedChange={setIsZoomed} />
        </div>
        {hasNext && !isZoomed && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(index + 1);
            }}
            title={t("ui.novee")}
            data-testid="media-viewer-next"
            style={{ ...chromeButtonStyle, ...navButtonStyle, right: space.xl }}
          >
            <IconChevronRight size={22} />
          </button>
        )}
      </div>
    </>
  );
}

/** Круглые полупрозрачные кнопки поверх тёмного фона (крестик, стрелки). */
const chromeButtonStyle: React.CSSProperties = {
  font: "inherit",
  fontSize: 18,
  width: 40,
  height: 40,
  flex: "0 0 40px",
  borderRadius: radius.circle,
  border: "none",
  background: "rgba(255, 255, 255, 0.14)",
  color: "#fff",
  cursor: "pointer",
  display: "grid",
  placeItems: "center",
};

const navButtonStyle: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  fontSize: 26,
  width: 48,
  height: 48,
  flex: "0 0 48px",
  zIndex: 1,
};

/**
 * Сцена слайда: ЧЕМ показывать решает msgtype события, а не MIME —
 * отправитель уже классифицировал файл (m.image/m.video/m.audio/m.file),
 * повторять его вывод по mimetype — значит ввести второе, иногда
 * расходящееся мнение.
 */
function Slide({
  media,
  url,
  error,
  onIsZoomedChange,
}: {
  media: ViewerMedia;
  url: string | null;
  error: string | null;
  /** Zoomable сообщает, увеличен ли слайд — ViewerBody прячет стрелки. */
  onIsZoomedChange: (zoomed: boolean) => void;
}) {
  const t = useT();
  const tfmt = useTF();
  if (!media.mxc || error) {
    return (
      <div
        style={{ color: "#fff", fontSize: font.small, textAlign: "center", padding: 24 }}
        data-testid="media-viewer-error"
      >
        {error
          ? tfmt("media.slideBrokenError", {
              icon: fileIconOf(media.name, media.mimetype),
              name: media.name,
              error,
            })
          : tfmt("media.slideBroken", {
              icon: fileIconOf(media.name, media.mimetype),
              name: media.name,
            })}
      </div>
    );
  }

  if (!url) {
    return (
      <div
        style={{ color: "rgba(255,255,255,0.8)", fontSize: font.small }}
        data-testid="media-viewer-loading"
      >{t("room.loadingShort")}</div>
    );
  }

  if (media.msgtype === "m.image") {
    // Зум — общий Zoomable (см. его шапку: щипок, колесо к курсору, двойной
    // тап/клик, таскание в увеличенном виде, пределы 1..5x, края клампятся).
    // Стрелки/крестик/шапка — снаружи этой области, их жесты не перехватываем.
    return (
      <Zoomable onIsZoomedChange={onIsZoomedChange}>
        <img
          src={url}
          alt={media.name}
          draggable={false}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            width: "auto",
            height: "auto",
            objectFit: "contain",
            display: "block",
          }}
          data-testid="media-viewer-image"
        />
      </Zoomable>
    );
  }
  if (media.msgtype === "m.video") {
    // autoPlay: пользователь только что кликнул превью — ждёт воспроизведения.
    // Если браузер его заблокирует (не было жеста из-за async-загрузки blob),
    // останутся видимые controls — нажать play вручную очевидно.
    return (
      <video
        src={url}
        controls
        autoPlay
        style={{ maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto", display: "block" }}
        data-testid="media-viewer-video"
      />
    );
  }
  if (media.msgtype === "m.audio") {
    // Без autoPlay: автозвук — первое, что режут браузеры, и единственное,
    // что раздражает, если вдруг сработает в открытом пространстве.
    return (
      <div style={{ textAlign: "center", color: "#fff" }}>
        <div style={{ fontSize: 48, marginBottom: space.lg }}>🎵</div>
        <audio src={url} controls data-testid="media-viewer-audio" />
      </div>
    );
  }
  // Файл без превью: большая иконка типа + имя + размер. Скачать/открыть —
  // кнопками сверху.
  return (
    <div
      style={{ textAlign: "center", color: "#fff", maxWidth: 360 }}
      data-testid="media-viewer-file"
    >
      <div style={{ fontSize: 72, lineHeight: 1.2 }} data-testid="media-viewer-file-icon">
        {fileIconOf(media.name, media.mimetype)}
      </div>
      <div style={{ fontSize: font.body, fontWeight: font.weightBold, wordBreak: "break-word" }}>
        {media.name}
      </div>
      <div style={{ fontSize: font.small, opacity: 0.75, marginTop: space.sm }}>
        {humanSize(media.size)}
        {media.mimetype && ` · ${media.mimetype}`}
      </div>
    </div>
  );
}

/**
 * Кнопки «Скачать» / «Поделиться» + плашка после скачивания.
 *
 * Blob приходит от ViewerBody: один авторизованный fetch на слайд остаётся
 * общим для шапки и сцены, а скачивание не делает второй сетевой запрос.
 */
function ViewerActions({
  blob,
  media,
}: {
  blob: Blob | null;
  media: ViewerMedia;
}) {
  const t = useT();
  /** Плашка результата скачивания; внешней ссылки в ней намеренно нет. */
  const [toast, setToast] = useState(false);
  /** true, если тост показан после НЕУДАВШЕГОСЯ шаринга (запасной путь — скачивание). */
  const [shareFallback, setShareFallback] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Поддерживает ли устройство шаринг ФАЙЛОВ. Проверка живёт в share.ts —
  // одном на весь клиент, чтобы кнопка-обманка не появилась ни здесь, ни в
  // контекстном меню сообщения (Антон, патч 10:24).
  const [canShare] = useState<boolean>(() => canShareFiles());

  useEffect(() => {
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  const onDownload = useCallback(() => {
    if (!blob || downloadBusy) return;
    setDownloadBusy(true);
    setDownloadError(null);
    void downloadReceivedAttachment(blob, media.name)
      .then((saved) => {
        if (!saved) return;
        if (toastTimer.current) clearTimeout(toastTimer.current);
        setShareFallback(false);
        setToast(true);
        toastTimer.current = setTimeout(() => setToast(false), 10_000);
      })
      .catch((downloadFailure: unknown) => {
        setDownloadError(
          downloadFailure instanceof Error ? downloadFailure.message : "The attachment could not be downloaded.",
        );
      })
      .finally(() => setDownloadBusy(false));
  }, [blob, downloadBusy, media.name]);

  const onShare = useCallback(async () => {
    if (!blob) return;
    // Маршрутизация (нативный мост оболочки → Web Share) — в share.ts.
    // Здесь только ЗАПАСНОЙ ПУТЬ: если шарить некуда, файл скачивается, и
    // пользователь видит ПОНЯТНОЕ сообщение, а не молчание (Антон 05.10).
    const outcome = await shareFile(blob, media.name, media.mimetype || blob.type || "");
    if (outcome !== "unavailable") return;
    setShareFallback(true);
    void downloadReceivedAttachment(blob, media.name)
      .then((saved) => {
        if (!saved) return;
        if (toastTimer.current) clearTimeout(toastTimer.current);
        setToast(true);
        toastTimer.current = setTimeout(() => {
          setToast(false);
          setShareFallback(false);
        }, 10_000);
      })
      .catch((downloadFailure: unknown) => {
        setShareFallback(false);
        setDownloadError(
          downloadFailure instanceof Error ? downloadFailure.message : "The attachment could not be downloaded.",
        );
      });
  }, [blob, media.name, media.mimetype]);

  return (
    <>
      <button
        type="button"
        onClick={onDownload}
        disabled={!blob || downloadBusy}
        title={t("media.download")}
        data-testid="media-viewer-download"
        style={{ ...chromeButtonStyle, opacity: blob && !downloadBusy ? 1 : 0.4 }}
      >
        <IconDownload size={18} />
      </button>
      {canShare && (
        <button
          type="button"
          onClick={() => void onShare()}
          disabled={!blob}
          title={t("media.share")}
          data-testid="media-viewer-share"
          style={{ ...chromeButtonStyle, opacity: blob ? 1 : 0.4 }}
        >
          {/* «Поделиться» = квадрат со стрелкой (отраслевой стандарт,
              постановка Антона 16:16 дословно). Прежняя «↗» читалась как
              «открыть в новой вкладке», а не как системный шаринг. */}
          <IconShare size={18} />
        </button>
      )}
      {(toast || downloadError) && (
        <div
          style={{
            // absolute, не fixed: плашка принадлежит просмотрщику, а он
            // теперь ограничен окном чата.
            position: "absolute",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            background: color.bg,
            color: color.text,
            border: `1px solid ${color.border}`,
            borderRadius: radius.md,
            padding: `${space.md}px ${space.lg}px`,
            fontSize: font.small,
            display: "flex",
            alignItems: "center",
            gap: space.lg,
            zIndex: 71,
            boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
          }}
          data-testid="media-viewer-toast"
          onClick={(e) => e.stopPropagation()}
        >
          <span role={downloadError ? "alert" : undefined}>
            {downloadError ?? (shareFallback ? t("media.shareFallbackDownloaded") : t("media.fileDownloaded"))}
          </span>
          <button
            type="button"
            onClick={() => {
              if (toastTimer.current) clearTimeout(toastTimer.current);
              setToast(false);
              setShareFallback(false);
              setDownloadError(null);
            }}
            style={{
              font: "inherit",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: color.textDim,
              padding: 0,
            }}
            data-testid="media-viewer-toast-close"
            title={t("app.hideActionError")}
          >
            <IconClose size={14} />
          </button>
        </div>
      )}
    </>
  );
}
